import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Award, ChevronLeft, CheckCircle2, DollarSign, Building2, User, Mail, Phone, Globe,
  ArrowRight, ArrowLeft, Star, Sparkles, AlertTriangle, Copy, CreditCard
} from 'lucide-react';
import LogoUploader from '../components/LogoUploader';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  Gold:   { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', accent: 'bg-amber-500' },
  Silver: { bg: 'bg-gray-300/10',  border: 'border-gray-400/30',  text: 'text-gray-300',  accent: 'bg-gray-400' },
  Bronze: { bg: 'bg-orange-600/10', border: 'border-orange-600/30', text: 'text-orange-400', accent: 'bg-orange-600' },
};

const SponsorApplicationPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [packages, setPackages] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [associations, setAssociations] = useState([]);
  const [step, setStep] = useState(1);
  const [reservationCode, setReservationCode] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);
  const [form, setForm] = useState({
    company_name: '', contact_name: '', contact_email: '', contact_phone: '',
    company_description: '', logo_url: '', website_url: '', affiliated_association: '',
    payment_reference: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventRes, pkgRes, assocRes, configRes] = await Promise.all([
          axios.get(`${API_URL}/activities/event/${eventId}`),
          axios.get(`${API_URL}/events/${eventId}/sponsorship-packages`),
          axios.get(`${API_URL}/associations`),
          axios.get(`${API_URL}/events/${eventId}/tent-config`)
        ]);
        setEvent(eventRes.data);
        setPackages(pkgRes.data.filter(p => p.active));
        setAssociations(assocRes.data);
        setConfig(configRes.data);
      } catch {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [eventId]);

  const getTierColor = (name) => TIER_COLORS[name] || { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', accent: 'bg-purple-500' };

  const handleContinueToPayment = (e) => {
    e.preventDefault();
    if (!selectedPkg) return;
    const code = `CCA-S-${crypto.randomUUID().replace(/-/g, '').slice(0, 8).toUpperCase()}`;
    setReservationCode(code);
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(reservationCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = reservationCode;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleSubmit = async () => {
    if (!form.payment_reference.trim()) {
      alert('Please enter your Interac e-Transfer confirmation number.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/events/${eventId}/sponsor-applications`, {
        ...form, package_id: selectedPkg.id
      });
      setSuccess(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-900">
      <Navbar variant="dark" />
      <div className="max-w-4xl mx-auto px-4 py-20"><div className="h-8 w-48 bg-white/10 rounded animate-pulse" /></div>
    </div>
  );

  // SUCCESS STATE
  if (success) return (
    <div className="min-h-screen bg-slate-900">
      <Navbar variant="dark" />
      <div className="max-w-2xl mx-auto px-4 py-20">
        <Card className="glass border-white/10">
          <CardContent className="p-8 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-cameroon-yellow/20 flex items-center justify-center mx-auto">
              <Sparkles className="w-8 h-8 text-cameroon-yellow" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-white">Application Submitted!</h2>
            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
              <p className="text-white/60 text-sm">Confirmation Number</p>
              <p className="text-2xl font-mono font-bold text-cameroon-yellow mt-1" data-testid="sponsor-confirmation">{success.confirmation_number}</p>
            </div>
            <div className="text-left p-4 rounded-lg bg-cameroon-green/10 border border-cameroon-green/20">
              <p className="text-cameroon-green font-semibold flex items-center gap-2"><CreditCard className="w-4 h-4" /> Payment Reference Submitted</p>
              <p className="text-white/70 text-sm mt-2">Your Interac e-Transfer confirmation: <strong className="text-white font-mono">{success.application?.payment_reference}</strong></p>
              <p className="text-white/70 text-sm mt-1">Amount: <strong className="text-white">${success.application?.amount?.toFixed(2)} CAD</strong></p>
              <p className="text-white/50 text-xs mt-2">Your sponsorship application is pending admin verification of your payment.</p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate(`/events/${eventId}`)} variant="outline" className="border-white/20 text-white hover:bg-white/10">Back to Event</Button>
              <Button onClick={() => navigate(`/reserve/track/${success.confirmation_number}`)} className="bg-cameroon-green hover:bg-cameroon-green/90">Track Application</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar variant="dark" />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Button variant="ghost" onClick={() => step === 2 ? setStep(1) : navigate(`/events/${eventId}`)} className="text-white/60 hover:text-white mb-6">
          <ChevronLeft className="w-4 h-4 mr-1" /> {step === 2 ? 'Back to Form' : 'Back to Event'}
        </Button>

        <div className="space-y-2 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-cameroon-yellow/20 flex items-center justify-center">
              <Award className="w-6 h-6 text-cameroon-yellow" />
            </div>
            <div>
              <h1 className="font-serif text-2xl font-bold text-white" data-testid="sponsor-page-title">Become a Sponsor</h1>
              <p className="text-white/60">{event?.title}</p>
            </div>
          </div>
          <p className="text-white/70 ml-15">Showcase your brand at Montreal's premier Cameroonian cultural event.</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-3 mb-8" data-testid="step-indicator">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${step === 1 ? 'bg-cameroon-yellow text-slate-900' : 'bg-white/10 text-white/50'}`}>
            <span className="w-5 h-5 rounded-full bg-black/10 flex items-center justify-center text-xs">1</span>
            Details
          </div>
          <ArrowRight className="w-4 h-4 text-white/30" />
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${step === 2 ? 'bg-cameroon-yellow text-slate-900' : 'bg-white/10 text-white/50'}`}>
            <span className="w-5 h-5 rounded-full bg-black/10 flex items-center justify-center text-xs">2</span>
            Payment & Submit
          </div>
        </div>

        {/* STEP 1: Package Selection + Company Info */}
        {step === 1 && (
          <>
            <div className="mb-8">
              <h3 className="font-semibold text-white mb-4">Select a Sponsorship Package</h3>
              <div className="grid gap-4">
                {packages.map((pkg) => {
                  const tc = getTierColor(pkg.name);
                  return (
                    <Card key={pkg.id} className={`cursor-pointer transition-all border-2 ${selectedPkg?.id === pkg.id ? `${tc.border} ${tc.bg}` : 'border-white/10 hover:border-white/20 glass'} ${pkg.spots_remaining === 0 ? 'opacity-50 cursor-not-allowed' : ''}`} onClick={() => pkg.spots_remaining > 0 && setSelectedPkg(pkg)} data-testid={`sponsor-pkg-${pkg.id}`}>
                      <CardContent className="p-5">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`w-10 h-10 rounded-lg ${tc.accent} flex items-center justify-center`}><Star className="w-5 h-5 text-white" /></div>
                            <div>
                              <h4 className={`font-bold text-lg ${tc.text}`}>{pkg.name} Package</h4>
                              <span className="text-white/50 text-sm">{pkg.spots_remaining} of {pkg.total_available} spots left</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-white">${pkg.price.toFixed(0)}</span>
                            <span className="text-white/40 text-sm ml-1">CAD</span>
                          </div>
                        </div>
                        {pkg.perks?.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {pkg.perks.map((perk, i) => (
                              <Badge key={`${pkg.id}-perk-${i}`} variant="outline" className={`border-white/10 ${tc.text} text-xs`}>
                                <CheckCircle2 className="w-3 h-3 mr-1" />{perk}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {pkg.spots_remaining === 0 && <Badge className="bg-red-500/20 text-red-400 border-0 mt-2">Sold Out</Badge>}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {selectedPkg && (
              <form onSubmit={handleContinueToPayment} className="space-y-6" data-testid="sponsor-application-form">
                <Card className="glass border-white/10">
                  <CardHeader><CardTitle className="text-white text-lg">Company Information</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white/80">Company Name *</Label>
                        <div className="relative"><Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" /><Input value={form.company_name} onChange={(e) => setForm(p => ({...p, company_name: e.target.value}))} required className="pl-10 bg-white/5 border-white/10 text-white" /></div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/80">Contact Name *</Label>
                        <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" /><Input value={form.contact_name} onChange={(e) => setForm(p => ({...p, contact_name: e.target.value}))} required className="pl-10 bg-white/5 border-white/10 text-white" /></div>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white/80">Email *</Label>
                        <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" /><Input type="email" value={form.contact_email} onChange={(e) => setForm(p => ({...p, contact_email: e.target.value}))} required className="pl-10 bg-white/5 border-white/10 text-white" /></div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/80">Phone *</Label>
                        <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" /><Input value={form.contact_phone} onChange={(e) => setForm(p => ({...p, contact_phone: e.target.value}))} required className="pl-10 bg-white/5 border-white/10 text-white" /></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/80">Affiliated / Referred by Association (Optional)</Label>
                      <select value={form.affiliated_association} onChange={(e) => setForm(p => ({...p, affiliated_association: e.target.value}))} className="w-full h-10 rounded-md border border-white/10 bg-white/5 text-white px-3 text-sm" data-testid="affiliated-association-select">
                        <option value="" className="bg-slate-800 text-white">-- None / Not affiliated --</option>
                        {associations.map(a => <option key={a.id} value={a.short_name || a.name} className="bg-slate-800 text-white">{a.short_name || a.name}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/80">Website</Label>
                      <div className="relative"><Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" /><Input value={form.website_url} onChange={(e) => setForm(p => ({...p, website_url: e.target.value}))} className="pl-10 bg-white/5 border-white/10 text-white" /></div>
                    </div>
                    <LogoUploader value={form.logo_url} onChange={(url) => setForm(p => ({...p, logo_url: url}))} label="Company Logo" dark={true} />
                    <div className="space-y-2">
                      <Label className="text-white/80">Company Description</Label>
                      <Textarea value={form.company_description} onChange={(e) => setForm(p => ({...p, company_description: e.target.value}))} className="bg-white/5 border-white/10 text-white" rows={3} />
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass border-cameroon-yellow/30">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/60 text-sm">Selected Package</p>
                        <p className="text-white font-semibold">{selectedPkg.name} Package</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white/60 text-sm">Amount</p>
                        <p className="text-2xl font-bold text-cameroon-yellow">${selectedPkg.price.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Button type="submit" className="w-full bg-cameroon-yellow hover:bg-cameroon-yellow/90 text-slate-900 h-12 text-base font-semibold" data-testid="sponsor-continue-payment-btn">
                  Continue to Payment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </form>
            )}
          </>
        )}

        {/* STEP 2: Payment Instructions + Interac Confirmation */}
        {step === 2 && selectedPkg && (
          <div className="space-y-6" data-testid="payment-step">
            <Card className="glass border-cameroon-yellow/30">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm">Sponsorship for</p>
                    <p className="text-white font-semibold">{selectedPkg.name} Package — {event?.title}</p>
                    <p className="text-white/50 text-sm">{form.company_name} ({form.contact_name})</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/60 text-sm">Total</p>
                    <p className="text-3xl font-bold text-cameroon-yellow">${selectedPkg.price.toFixed(2)}</p>
                    <p className="text-white/40 text-xs">CAD</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-white/10">
              <CardContent className="p-6 space-y-4">
                <div className="text-center">
                  <p className="text-white/60 text-sm mb-1">Your Unique Sponsorship Code</p>
                  <div className="flex items-center justify-center gap-3">
                    <p className="text-3xl font-mono font-bold text-cameroon-yellow tracking-wider" data-testid="reservation-code">{reservationCode}</p>
                    <Button variant="ghost" size="sm" onClick={handleCopyCode} className="text-white/40 hover:text-white"><Copy className="w-4 h-4" /></Button>
                  </div>
                  {codeCopied && <p className="text-cameroon-green text-xs mt-1">Copied!</p>}
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardHeader>
                <CardTitle className="text-amber-300 flex items-center gap-2 text-lg">
                  <DollarSign className="w-5 h-5" />
                  Interac e-Transfer Payment Instructions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {config?.payment_instructions && <p className="text-white/80 text-sm">{config.payment_instructions}</p>}

                {/* Send to details - prominent display */}
                {(config?.payment_email || config?.payment_phone) ? (
                  <div className="p-4 rounded-lg bg-white/10 border border-white/20 space-y-3" data-testid="payment-send-to">
                    <p className="text-white font-semibold text-sm uppercase tracking-wide">Send your Interac e-Transfer to:</p>
                    {config.payment_account_name && (
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-cameroon-green/10 border border-cameroon-green/20">
                        <div className="w-10 h-10 rounded-lg bg-cameroon-green/20 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-cameroon-green" />
                        </div>
                        <div>
                          <p className="text-white/50 text-xs">
                            {config.payment_account_type === 'cca_owned'
                              ? 'CCA Montreal Account (Auto-Deposit)'
                              : 'Event Host Association Account (Auto-Deposit)'}
                          </p>
                          <p className="text-white font-bold text-lg" data-testid="payment-account-name">{config.payment_account_name}</p>
                          <p className="text-cameroon-green/80 text-xs mt-0.5">
                            {config.payment_account_type === 'cca_owned'
                              ? 'This is an official CCA Montreal account. Your payment goes directly to the Council of Cultural Associations.'
                              : 'This is the account of the association hosting this event. Payments are collected by the host on behalf of CCA.'}
                          </p>
                        </div>
                      </div>
                    )}
                    {config.payment_email && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-cameroon-green/20 flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-cameroon-green" />
                        </div>
                        <div>
                          <p className="text-white/50 text-xs">Email</p>
                          <p className="text-white font-bold text-lg" data-testid="payment-email">{config.payment_email}</p>
                        </div>
                      </div>
                    )}
                    {config.payment_phone && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                          <Phone className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-white/50 text-xs">{config.payment_email ? 'Or via Phone' : 'Phone'}</p>
                          <p className="text-white font-bold text-lg" data-testid="payment-phone">{config.payment_phone}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="payment-not-configured">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-300 font-semibold text-sm">Payment details not yet configured</p>
                        <p className="text-white/60 text-sm mt-1">The event organizer has not yet provided an Interac e-Transfer email or phone number. Please contact the event organizer for payment details before proceeding.</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-4 rounded-lg bg-cameroon-yellow/10 border border-cameroon-yellow/30">
                  <p className="text-white/50 text-xs mb-1">Amount to send</p>
                  <p className="text-3xl font-bold text-cameroon-yellow">${selectedPkg.price.toFixed(2)} CAD</p>
                </div>
                <div className="p-4 rounded-lg bg-cameroon-red/10 border border-cameroon-red/30">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-cameroon-red flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-cameroon-red font-bold text-sm">IMPORTANT: Include Your Sponsorship Code</p>
                      <p className="text-white/70 text-sm mt-1">
                        You <strong className="text-white">must</strong> include your sponsorship code <strong className="text-cameroon-yellow font-mono">{reservationCode}</strong> as the <strong className="text-white">message or reference</strong> in your Interac e-Transfer so your payment can be verified quickly.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-cameroon-yellow" />
                  Enter Your Payment Confirmation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-white/60 text-sm">After sending your Interac e-Transfer, enter the confirmation number you received from your bank below.</p>
                <div className="space-y-2">
                  <Label className="text-white/80">Interac e-Transfer Confirmation Number *</Label>
                  <Input
                    value={form.payment_reference}
                    onChange={(e) => setForm(p => ({...p, payment_reference: e.target.value}))}
                    placeholder="e.g. CA1234567890"
                    className="bg-white/5 border-white/10 text-white h-12 text-lg font-mono"
                    required
                    data-testid="interac-confirmation-input"
                  />
                  <p className="text-white/40 text-xs">This is the confirmation number your bank provides after you complete the e-Transfer.</p>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="border-white/20 text-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !form.payment_reference.trim()}
                className="flex-1 bg-cameroon-yellow hover:bg-cameroon-yellow/90 text-slate-900 h-12 text-base font-semibold"
                data-testid="sponsor-submit-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Sponsorship Application'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SponsorApplicationPage;
